import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const THEMES = {
  DEFAULT: 'default',
  LIGHT: 'light',
  NAVY: 'navy',
  DRACULA: 'dracula',
  FOREST: 'forest',
  TEAL: 'teal',
  SUNSET: 'sunset',
  MIDNIGHT: 'midnight',
  NORD: 'nord',
  ROSEWATER: 'rosewater',
};

export const SIDEBAR_MODES = {
  MODERN: 'modern',
  COMPACT: 'compact',
  CLASSIC: 'classic',
  FLOATING: 'floating',
  GLASS: 'glass',
};

export const MOBILE_LAYOUTS = {
  BOTTOM_NAV: 'bottom-nav',
  SIDEBAR: 'sidebar',
};

export const FONTS = {
  INTER: 'Inter',
  ROBOTO: 'Roboto',
  POPPINS: 'Poppins',
  PLAYFAIR: 'Playfair Display',
  SPACE_GROTESK: 'Space Grotesk',
  DM_SANS: 'DM Sans',
};

export const CARD_SHAPES = {
  SLEEK: 'sleek',
  ROUND: 'round',
  SHARP: 'sharp',
  MINIMAL: 'minimal',
};

export const CHART_STYLES = {
  MODERN: 'modern',
  ELEGANT: 'elegant',
  MINIMAL: 'minimal',
};

export const TABLE_STYLES = {
  STANDARD: 'standard',
  COMPACT: 'compact',
  STRIPED: 'striped',
  BORDERED: 'bordered',
};

export const UI_ARCHITECTURES = {
  STANDARD: 'standard',
  NEUMORPHIC: 'neumorphic',
  CLAY: 'clay',
  CYBERNEON: 'cyberneon',
  BRUTALISM: 'brutalism',
  NEOBRUTALISM: 'neobrutalism',
  APPLE_GLASS: 'apple_glass',
  ORIGAMI: 'origami',
};

export function ThemeProvider({ children }) {
  // Ler do localStorage ou usar default
  const [theme, setTheme] = useState(() => localStorage.getItem('kumbuos_theme') || THEMES.DEFAULT);
  const [sidebarMode, setSidebarMode] = useState(() => localStorage.getItem('kumbuos_sidebar_mode') || SIDEBAR_MODES.MODERN);
  const [mobileLayout, setMobileLayout] = useState(() => localStorage.getItem('kumbuos_mobile_layout') || MOBILE_LAYOUTS.BOTTOM_NAV);
  const [cardShape, setCardShape] = useState(() => localStorage.getItem('kumbuos_card_shape') || CARD_SHAPES.SLEEK);
  const [font, setFont] = useState(() => localStorage.getItem('kumbuos_font') || FONTS.DM_SANS);
  const [chartStyle, setChartStyle] = useState(() => localStorage.getItem('kumbuos_chart_style') || CHART_STYLES.MODERN);
  const [tableStyle, setTableStyle] = useState(() => localStorage.getItem('kumbuos_table_style') || TABLE_STYLES.STANDARD);
  const [uiArchitecture, setUiArchitecture] = useState(() => localStorage.getItem('kumbuos_ui_architecture') || UI_ARCHITECTURES.STANDARD);

  // Aplicar tema e estilos ao elemento HTML/localStorage
  useEffect(() => {
    const root = window.document.documentElement;
    root.setAttribute('data-theme', theme);
    localStorage.setItem('kumbuos_theme', theme);
  }, [theme]);

  // Aplicar fonte
  useEffect(() => {
    const root = window.document.documentElement;
    root.style.setProperty('--font-sans', `"${font}", sans-serif`);
    localStorage.setItem('kumbuos_font', font);
  }, [font]);

  useEffect(() => {
    localStorage.setItem('kumbuos_sidebar_mode', sidebarMode);
  }, [sidebarMode]);

  useEffect(() => {
    localStorage.setItem('kumbuos_mobile_layout', mobileLayout);
  }, [mobileLayout]);

  useEffect(() => {
    localStorage.setItem('kumbuos_card_shape', cardShape);
  }, [cardShape]);

  useEffect(() => {
    localStorage.setItem('kumbuos_chart_style', chartStyle);
  }, [chartStyle]);

  useEffect(() => {
    localStorage.setItem('kumbuos_table_style', tableStyle);
  }, [tableStyle]);

  useEffect(() => {
    localStorage.setItem('kumbuos_ui_architecture', uiArchitecture);
    // Também adicionamos uma classe global no body para facilitar estilização profunda
    document.body.className = document.body.className.replace(/arch-\w+/g, '').trim();
    document.body.classList.add(`arch-${uiArchitecture}`);
  }, [uiArchitecture]);

  const value = {
    theme,
    setTheme,
    themes: THEMES,
    sidebarMode,
    setSidebarMode,
    sidebarModes: SIDEBAR_MODES,
    mobileLayout,
    setMobileLayout,
    mobileLayouts: MOBILE_LAYOUTS,
    cardShape,
    setCardShape,
    cardShapes: CARD_SHAPES,
    font,
    setFont,
    fonts: FONTS,
    chartStyle,
    setChartStyle,
    chartStyles: CHART_STYLES,
    tableStyle,
    setTableStyle,
    tableStyles: TABLE_STYLES,
    uiArchitecture,
    setUiArchitecture,
    uiArchitectures: UI_ARCHITECTURES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
